import style from "./edit-sensor.module.css";

import { inject, observer } from "mobx-react";
import SensorIdInput from "./components/sensor-id-input";
import TypeDropDown from "./components/type-dropdown";
import StatusToggle from "./components/status-toggle";
import { SensorDataStore } from "../../store/store";
import { Ref, useEffect, useRef } from "react";
import CancelButton from "../../../../components/buttons/CancelButton";
import SaveButton from "../../../../components/buttons/SaveButton";
import { scrollIntoViewAndAlign } from "../../../../utils/scroll";
import { useTranslation } from "react-i18next";
import { runInAction } from "mobx";
import LocationSelector, {
    LocationItem,
} from "../../../../components/location-selector/LocationSelector";
import LocationSelectorStore from "../../../../store/location-selector-store";
import { TreeNodeItem } from "../../../../components/tree-select/TreeSelect";
import HearbeatToggle from "./components/hearbeat-toggle";
import HeartbeatMinInput from "./components/heartbeat-min-input";
import classNames from "classnames";

interface EditSensorProps {
    sensorDataStore?: SensorDataStore;
    locationSelectorStore?: LocationSelectorStore;
    saveLocked?: boolean;
    onSaveClick(): void;
    onCancelClick(): void;
}

function EditSensor(props: EditSensorProps) {
    const editComponentElement: Ref<any> = useRef(null);
    const sensorDataStore = props.sensorDataStore!;
    const locationSelectorStore = props.locationSelectorStore!;

    const { saveLocked, onSaveClick, onCancelClick } = props;
    const { t } = useTranslation("common");
    const { errorMessages } = sensorDataStore;

    const isVirtualSensor =
        sensorDataStore.selectedSensor.sensorType === "VirtualSensor";
    const isToggleEnabled = sensorDataStore.selectedSensor.isHeartbeatMonitored;

    useEffect(() => {
        scrollIntoViewAndAlign(editComponentElement.current);

        return function unmount() {
            runInAction(() => {
                sensorDataStore.errors = null;
                sensorDataStore.errorMessages = null;
                sensorDataStore.setValidated(true);
            });
        };
    }, []);

    const locationList = () => {
        const locationItemList: LocationItem[] =
            locationSelectorStore.selectedRoomList.map((room: TreeNodeItem) => {
                return {
                    locationId: room.id,
                    locationName: room.name,
                    areaName: room.path || "",
                    hasTelecareCapability: room.hasTelecareCapability,
                };
            });

        return locationItemList;
    };

    const setSelectedSensorLocation = (value: LocationItem) => {
        if (value === null || value.locationId === null) {
            sensorDataStore!.setSelectedSensorLocationData(0, "", "", false);
            sensorDataStore!.validateSelectedSensor("locationId");
            sensorDataStore.validateSelectedSensor("sensorType");
            return;
        }

        const selectedLocationItem =
            locationSelectorStore.selectedRoomList.find((room) => {
                return value.locationId === room.id;
            });

        const hasTelecareCapability =
            selectedLocationItem!.hasTelecareCapability!;

        sensorDataStore.setSelectedSensorLocationData(
            value.locationId,
            value.locationName,
            value.areaName,
            hasTelecareCapability
        );

        sensorDataStore.setSelectedSensorHasChanges(true);
        sensorDataStore.validateSelectedSensor("locationId");
        sensorDataStore.validateSelectedSensor("sensorType");
    };

    const render = () => {
        return (
            <>
                <tr
                    className={classNames(style["edit-row"], style["top-row"], {
                        [style["bottom-row"]]: !isVirtualSensor,
                    })}
                    ref={editComponentElement}
                >
                    <td></td>
                    <td>
                        <div className={style["column"]}>
                            <StatusToggle />
                        </div>
                    </td>

                    <td>
                        <div className={style["column"]}>
                            <SensorIdInput />
                        </div>
                    </td>
                    <td>
                        <div className={style["column"]}>
                            <TypeDropDown />
                        </div>
                    </td>

                    <td>
                        <div className={style["column"]}>
                            <LocationSelector
                                mandatory
                                label={t("Room").toUpperCase()}
                                selectedId={
                                    sensorDataStore.selectedSensor.locationId
                                }
                                locationList={locationList()}
                                onValueChange={(value: LocationItem) => {
                                    setSelectedSensorLocation(value);
                                    sensorDataStore.validateSelectedSensor(
                                        "locationId"
                                    );
                                    sensorDataStore.setSelectedSensorHasChanges(
                                        true
                                    );
                                }}
                                errorMessage={
                                    (errorMessages &&
                                        errorMessages["locationId"] &&
                                        t("Required information")) ||
                                    ""
                                }
                            />
                        </div>

                        <div className={style["column"]} />
                    </td>
                    <td />

                    <td className={style["action-column"]}>
                        <div className={style["action-buttons-container"]}>
                            <div className={style["action-buttons"]}>
                                <CancelButton
                                    onClick={() => {
                                        onCancelClick();
                                    }}
                                >
                                    {t("Cancel")}
                                </CancelButton>

                                <SaveButton
                                    onClick={() => {
                                        onSaveClick();
                                    }}
                                    disabled={saveLocked}
                                >
                                    {t("Save")}
                                </SaveButton>
                            </div>
                        </div>
                    </td>
                </tr>
                {isVirtualSensor && (
                    <tr
                        className={classNames(
                            style["edit-row"],
                            style["bottom-row"]
                        )}
                    >
                        <td></td>
                        <td>
                            <div className={style["heartbeat-container"]}>
                                <div className={style["column"]}>
                                    <HearbeatToggle />
                                </div>
                            </div>
                        </td>
                        <td>
                            <div className={style["heartbeat-container"]}>
                                <div className={style["column"]}>
                                    <HeartbeatMinInput
                                        disabled={!isToggleEnabled}
                                    />
                                </div>
                            </div>
                        </td>
                        <td colSpan={4}></td>
                    </tr>
                )}
            </>
        );
    };

    return render();
}

export default inject(
    "sensorDataStore",
    "locationSelectorStore"
)(observer(EditSensor));
